import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  ArrowRightIcon,
  Box,
  ChevronDownIcon,
  CloseIcon,
  Column,
  DottedCircleIcon,
  IconButton,
  Row,
  SuccessIcon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@hightouch/htui";

import { Icon as DecisionEngineIcon } from "src/components/layout/nav/icons/decision-engine";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { useUser } from "src/contexts/user-context";
import { useNavigate } from "src/router";
import {
  DecisionEngineFlowQuery,
  useDecisionEngineFeatureMatricesForEngineQuery,
  useDecisionEngineFlowQuery,
} from "src/graphql";
import { DecisionEngineStatus } from "@hightouch/lib/customer-data/decision-engine/types";
import type { DecisionEngine } from "src/pages/decision-engines";

type AgentSetupPanelContext = {
  openSetupPanel: (flowId: string) => void;
};

const AgentSetupPanelCtx = createContext<AgentSetupPanelContext | null>(null);

export function AgentSetupPanelProvider({
  value,
  children,
}: {
  value: AgentSetupPanelContext;
  children: ReactNode;
}) {
  return (
    <AgentSetupPanelCtx.Provider value={value}>
      {children}
    </AgentSetupPanelCtx.Provider>
  );
}

export function useAgentSetupPanel(): AgentSetupPanelContext {
  const ctx = useContext(AgentSetupPanelCtx);
  if (!ctx) {
    throw new Error(
      "useAgentSetupPanel must be used within an AgentSetupPanelProvider",
    );
  }
  return ctx;
}

type Flow = NonNullable<DecisionEngineFlowQuery["decision_engine_flows_by_pk"]>;

type Navigate = ReturnType<typeof useNavigate>;

type SetupChecklistItemProps = {
  title: string;
  description: ReactNode;
  isComplete: boolean;
  onClick?: () => void;
};

type ChecklistItem = SetupChecklistItemProps & {
  id?: string | number;
};

const isChecklistComplete = (items: ChecklistItem[]): boolean =>
  items.every((item) => item.isComplete);

type SetupTab = {
  label: string;
  items: ChecklistItem[];
};

const firstIncompleteTabIndex = (tabs: SetupTab[]): number => {
  const index = tabs.findIndex((tab) => !isChecklistComplete(tab.items));
  return index === -1 ? 0 : index;
};

type Props = {
  flowId: string;
  isMinimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  onClose: () => void;
};

export const AgentSetupPanel: FC<Readonly<Props>> = ({
  flowId,
  isMinimized,
  onMinimize,
  onExpand,
  onClose,
}) => {
  const { user } = useUser();
  const { engine } = useDecisionEngineContext();
  const navigate = useNavigate();
  const showAdminTab = Boolean(user?.can_impersonate);

  const { data: flow, isSuccess } = useDecisionEngineFlowQuery(
    { id: flowId },
    {
      select: (data) => data.decision_engine_flows_by_pk,
      meta: {
        isFastEndpoint: true,
      },
    },
  );

  const { data: featureMatrices, isFetched: featureMatricesFetched } =
    useDecisionEngineFeatureMatricesForEngineQuery(
      { decisionEngineId: engine.id },
      {
        select: (data) => data.decision_engine_feature_matrices,
        meta: {
          isFastEndpoint: true,
        },
      },
    );

  const engineHasFeatureMatrix = Boolean(featureMatrices?.length);

  // Clear the parent's open state when the flow doesn't exist or is no longer
  // in setup, so it doesn't linger as "open" with nothing rendered.
  const isIneligible =
    isSuccess && (!flow || flow.status === DecisionEngineStatus.READY);

  useEffect(() => {
    if (isIneligible) {
      onClose();
    }
  }, [isIneligible, onClose]);

  if (!flow || flow.status === DecisionEngineStatus.READY) {
    return null;
  }

  const tabs: SetupTab[] = [
    {
      label: "1. Connect data",
      items: getConnectDataItems(flow, engineHasFeatureMatrix, navigate),
    },
    {
      label: "2. Prepare agent",
      items: getPrepareAgentItems(flow, navigate),
    },
    {
      label: "3. Add messages",
      items: getAddMessagesItems(flow, navigate),
    },
  ];
  if (showAdminTab) {
    tabs.push({
      label: "4. Admin",
      items: getAdminItems(flow, engine, navigate),
    });
  }

  return (
    <AgentSetupPanelContent
      key={`${flow.id}-${featureMatricesFetched}`}
      flowName={flow.name}
      tabs={tabs}
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      onExpand={onExpand}
      onClose={onClose}
    />
  );
};

type AgentSetupPanelContentProps = {
  flowName: string;
  tabs: SetupTab[];
  isMinimized: boolean;
  onMinimize: () => void;
  onExpand: () => void;
  onClose: () => void;
};

const AgentSetupPanelContent: FC<Readonly<AgentSetupPanelContentProps>> = ({
  flowName,
  tabs,
  isMinimized,
  onMinimize,
  onExpand,
  onClose,
}) => {
  const [tabIndex, setTabIndex] = useState(() => firstIncompleteTabIndex(tabs));

  if (isMinimized) {
    return (
      <AgentSetupMinimizedBar
        flowName={flowName}
        onExpand={onExpand}
        onClose={onClose}
      />
    );
  }

  return (
    <Column
      position="fixed"
      bottom={0}
      right={6}
      width="500px"
      height="560px"
      maxHeight="70vh"
      bg="white"
      border="1px solid"
      borderBottom="none"
      borderColor="base.border"
      borderTopRadius="xl"
      boxShadow="lg"
      zIndex="docked"
      overflow="hidden"
    >
      <Row align="center" gap={2} pt={4} px={4} pb={2}>
        <DecisionEngineIcon flexShrink={0} boxSize={6} />
        <Text size="lg" fontWeight="medium" isTruncated>
          Agent setup: {flowName}
        </Text>
        <Row align="center" gap={1} ml="auto" flexShrink={0}>
          <IconButton
            aria-label="Minimize agent setup"
            icon={ChevronDownIcon}
            onClick={onMinimize}
          />
          <IconButton
            aria-label="Close agent setup"
            icon={CloseIcon}
            onClick={onClose}
          />
        </Row>
      </Row>
      <Column
        as={Tabs}
        flex={1}
        minHeight={0}
        index={tabIndex}
        // @ts-expect-error onChange type is not properly typed when using `as`
        onChange={setTabIndex}
      >
        <Box as={TabList} px={5}>
          {tabs.map((tab) => (
            <Tab
              key={tab.label}
              statusIndicator={completeIndicator(
                isChecklistComplete(tab.items),
              )}
            >
              {tab.label}
            </Tab>
          ))}
        </Box>
        <Column as={TabPanels} flex={1} minHeight={0} overflowY="auto">
          {tabs.map((tab) => (
            <TabPanel key={tab.label}>
              <SetupChecklist items={tab.items} />
            </TabPanel>
          ))}
        </Column>
      </Column>
    </Column>
  );
};

type AgentSetupMinimizedBarProps = {
  flowName: string;
  onExpand: () => void;
  onClose: () => void;
};

const AgentSetupMinimizedBar: FC<Readonly<AgentSetupMinimizedBarProps>> = ({
  flowName,
  onExpand,
  onClose,
}) => (
  <Row
    position="fixed"
    bottom={24}
    right={6}
    maxWidth="xs"
    align="center"
    gap={2}
    py={1.5}
    pl={3}
    pr={1.5}
    bg="white"
    border="1px solid"
    borderColor="base.border"
    borderRadius="full"
    boxShadow="md"
    zIndex="docked"
  >
    <Row
      as="button"
      type="button"
      aria-label={`Expand agent setup: ${flowName}`}
      align="center"
      gap={2}
      flex={1}
      minWidth={0}
      borderRadius="full"
      cursor="pointer"
      _hover={{ bg: "base.backgroundHover" }}
      onClick={onExpand}
    >
      <DecisionEngineIcon flexShrink={0} boxSize={5} />
      <Text size="sm" fontWeight="medium" isTruncated>
        Agent setup: {flowName}
      </Text>
    </Row>
    <IconButton
      aria-label="Close agent setup"
      size="sm"
      icon={CloseIcon}
      flexShrink={0}
      onClick={onClose}
    />
  </Row>
);

function getConnectDataItems(
  flow: Flow,
  engineHasFeatureMatrix: boolean,
  navigate: Navigate,
): ChecklistItem[] {
  const hasAudience = Boolean(flow.audience?.id);
  const hasFeatureMatrix = Boolean(flow.decision_engine_feature_matrix);

  return [
    {
      title: "Link audience",
      description: "Link the audience for your agent from Customer Studio.",
      isComplete: hasAudience,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration#targeting`),
    },
    {
      title: "Create feature matrix",
      description:
        "Create a feature matrix to decide which user features should be used for optimizing decisions.",
      isComplete: engineHasFeatureMatrix,
      onClick: () => navigate(`/ai/configuration/feature-matrices`),
    },
    {
      title: "Link feature matrix",
      description: "Link a feature matrix to your agent.",
      isComplete: hasFeatureMatrix,
      onClick: () =>
        navigate(`/ai/flows/${flow.id}/configuration#feature-matrix`),
    },
  ];
}

function getPrepareAgentItems(flow: Flow, navigate: Navigate): ChecklistItem[] {
  const customerManagedPercentage =
    flow.experiments.find((e) => e.name === "customer_managed")
      ?.audience_percent ?? 0;
  const hasName = Boolean(flow.name);
  // holdout and customer managed of 0% should be flagged as incomplete on new agents
  const hasHoldout = Boolean(flow.config?.holdout);
  const hasCustomerManaged = Boolean(customerManagedPercentage);
  const hasOutcomes = Boolean(flow.outcomes.length);
  const hasValidScheduling =
    Boolean(flow.timing?.days?.length) && Boolean(flow.timing?.times?.length);
  const hasCustomerManagedCampaignIds = Boolean(
    flow.config?.customer_managed_campaign_ids?.length,
  );

  return [
    {
      title: "Set agent name",
      description: "Set the agent name to distinguish it from others.",
      isComplete: hasName,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration`),
    },
    {
      title: "Set holdout",
      description:
        "Set a percentage of traffic to be held out from the experiment. We usually recommend 5% or 10% depending on the audience size.",
      isComplete: hasHoldout,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration#targeting`),
    },
    {
      title: "Set customer managed",
      description: "Set a percentage of traffic to receive your BAU comms.",
      isComplete: hasCustomerManaged,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration#targeting`),
    },
    {
      title: "Set goals",
      description:
        "Set your agent goals so personalization can drive your desired outcomes.",
      isComplete: hasOutcomes,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration#goals`),
    },
    {
      title: "Set schedule",
      description: "Set at least one valid day/time for message scheduling.",
      isComplete: hasValidScheduling,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration#scheduling`),
    },
    {
      title: "Set customer managed campaign IDs",
      description:
        "Set these IDs to power our performance insights for the customer managed sends.",
      isComplete: hasCustomerManagedCampaignIds,
      onClick: () => navigate(`/ai/flows/${flow.id}/configuration#advanced`),
    },
  ];
}

function getAddMessagesItems(flow: Flow, navigate: Navigate): ChecklistItem[] {
  // Channels are scoped to this flow's (non-deleted) messages
  const flowChannels = Array.from(
    new Map(
      flow.messages
        .filter((m) => !m.message.deleted_at)
        .map((m) => [m.message.channel.id, m.message.channel]),
    ).values(),
  );
  const hasChannels = flowChannels.length > 0;
  const channelsWithoutSendAttribution = flowChannels.filter(
    (c) => !c.send_event_id,
  );
  const hasEnabledMessages = Boolean(
    flow.messages.filter((m) => m.enabled && !m.message.deleted_at).length,
  );

  const items: ChecklistItem[] = [
    {
      title: "Create channels",
      description: "Create at least one channel for this agent.",
      isComplete: hasChannels,
      onClick: () => navigate(`/ai/configuration/channels`),
    },
  ];

  if (hasChannels && channelsWithoutSendAttribution.length === 0) {
    items.push({
      title: "Add send attribution to all channels",
      description: "All channels should have send attribution.",
      isComplete: true,
    });
  } else {
    for (const c of channelsWithoutSendAttribution) {
      items.push({
        id: c.id,
        title: `Add send attribution to channel: ${
          c.label ?? c.destination.name ?? c.destination.definition.name
        }`,
        description: "All channels should have send attribution.",
        isComplete: false,
        onClick: () => navigate(`/ai/configuration/channels/${c.id}`),
      });
    }
  }

  items.push({
    title: "Create and enable messages",
    description: "Create and enable at least one message for this agent.",
    isComplete: hasEnabledMessages,
    onClick: () => navigate(`/ai/flows/${flow.id}/messages`),
  });

  return items;
}

function getAdminItems(
  flow: Flow,
  engine: DecisionEngine,
  navigate: Navigate,
): ChecklistItem[] {
  const engineHasName = Boolean(engine.config?.name);
  const hasSlackChannel = Boolean(engine.config?.slack_channel);
  const hasMessageCompletionIntervalDays = Boolean(
    engine.config?.message_completion_interval_days,
  );
  const hasAttribution =
    Boolean(flow.config?.attribution?.window?.unit) &&
    Boolean(flow.config?.attribution?.window?.value);

  const uniformPercentage =
    flow.experiments.find((e) => e.name === "uniform")?.audience_percent ?? 0;
  const hasUniform = Boolean(uniformPercentage);

  return [
    {
      title: "Add engine name",
      description:
        "Add the name to the engine. This should usually match the customer's name",
      isComplete: engineHasName,
      onClick: () => navigate(`/ai/admin`),
    },
    {
      title: "Add Slack channel to engine",
      description:
        "Add the Slack channel to the engine for plots. Our default channel ID is C07RGLSVATU.",
      isComplete: hasSlackChannel,
      onClick: () => navigate(`/ai/admin`),
    },
    {
      title: "Add message completion interval days",
      description:
        "Set the message completion interval days on the engine. Our standard default is 3.",
      isComplete: hasMessageCompletionIntervalDays,
      onClick: () => navigate(`/ai/admin`),
    },
    {
      title: "Add flow attribution",
      description:
        "Set the attribution window for attributing goal events back to interactions.",
      isComplete: hasAttribution,
      onClick: () => navigate(`/ai/admin/agents?flowId=${flow.id}#attribution`),
    },
    {
      title: "Set uniform",
      description:
        "Set a percentage of traffic to receive uniform decisioning. We usually recommend 5% or 10% depending on the treatment portion of the audience.",
      isComplete: hasUniform,
      onClick: () => navigate(`/ai/admin/agents?flowId=${flow.id}#experiments`),
    },
  ];
}

const completeIndicator = (isComplete: boolean): ReactNode =>
  isComplete ? (
    <Box as={SuccessIcon} boxSize={4} color="success.base" flexShrink={0} />
  ) : undefined;

const SetupChecklist: FC<Readonly<{ items: ChecklistItem[] }>> = ({
  items,
}) => (
  <>
    {items.map(({ id, ...item }) => (
      <SetupChecklistItem key={id ?? item.title} {...item} />
    ))}
  </>
);

const SetupChecklistItem: FC<Readonly<SetupChecklistItemProps>> = ({
  title,
  description,
  isComplete,
  onClick,
}) => (
  <Row
    gap={2}
    px={3}
    py={2}
    borderRadius="xl"
    align="flex-start"
    role="group"
    cursor={onClick ? "pointer" : undefined}
    _hover={onClick ? { bg: "base.backgroundHover" } : undefined}
    onClick={onClick}
  >
    {isComplete ? (
      <Box as={SuccessIcon} boxSize={5} color="success.base" flexShrink={0} />
    ) : (
      <Box
        as={DottedCircleIcon}
        boxSize={5}
        color="text.tertiary"
        flexShrink={0}
      />
    )}
    {isComplete ? (
      <Box color="text.disabled" textDecoration="line-through">
        {title}
      </Box>
    ) : (
      <Column flex={1} minWidth={0}>
        <Text>{title}</Text>
        <Text size="sm" color="text.secondary">
          {description}
        </Text>
      </Column>
    )}
    {onClick && (
      <Box
        as={ArrowRightIcon}
        boxSize={4}
        ml="auto"
        flexShrink={0}
        opacity={0}
        _groupHover={{ opacity: 1 }}
      />
    )}
  </Row>
);
